/** @file bookings/crud.js — CRUD, lifecycle view, rebook. */
import { supabase } from "@/lib/supabaseClient";
import { buildBookingPayload } from "./payload";

/* =====================  BOOKINGS  ========================== */

export async function listBookings({
  search = "",
  siteId = null,
  status = null,
  direction = null,
  dateFrom = null,
  dateTo = null,
  clientId = null,
  incompleteOnly = false,
  onHold = null,
  driverTbaOnly = false,
} = {}) {
  let q = supabase
    .from("lbs_bookings")
    .select(
      `id, reference_number, scheduled_start, scheduled_end, status, direction,
       sender_name, sender_company, receiver_name, receiver_company,
       vehicle_type, vehicle_regos, pallet_type, pallets_in, pallets_out,
       title, client_id, created_at,
       external_ref, internal_ref, driver_tba,
       on_hold, hold_reason, booker_update_requested_at,
       site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code,timezone,state),
       bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,code,color)`
    )
    .order("scheduled_start", { ascending: false })
    .limit(200);

  if (search) {
    q = q.or(
      `reference_number.ilike.%${search}%,sender_name.ilike.%${search}%,receiver_name.ilike.%${search}%,po_number.ilike.%${search}%,external_ref.ilike.%${search}%,internal_ref.ilike.%${search}%,title.ilike.%${search}%`
    );
  }
  if (clientId) q = q.eq("client_id", clientId);
  if (siteId) q = q.eq("site_id", siteId);
  if (status) q = q.eq("status", status);
  if (direction) q = q.eq("direction", direction);
  if (dateFrom) q = q.gte("scheduled_start", `${dateFrom}T00:00:00.000Z`);
  if (dateTo)   q = q.lte("scheduled_start", `${dateTo}T23:59:59.999Z`);
  if (onHold === true)  q = q.eq("on_hold", true);
  if (onHold === false) q = q.eq("on_hold", false);
  if (driverTbaOnly)    q = q.eq("driver_tba", true);   // Phase 9.3
  if (incompleteOnly) {
    q = q.or(
      "direction.is.null,sender_company.is.null,sender_company.eq.,receiver_company.is.null,receiver_company.eq.,vehicle_type.is.null,vehicle_type.eq.,pallet_type.is.null,pallet_type.eq.,and(pallets_in.is.null,pallets_out.is.null),and(pallets_in.eq.0,pallets_out.eq.0)"
    );
  }
  return q;
}

export async function getBooking(id) {
  return supabase.from("lbs_bookings").select("*").eq("id", id).maybeSingle();
}

export async function createBooking(values, currentUserId) {
  const payload = await buildBookingPayload(values, currentUserId, true);
  return supabase
    .from("lbs_bookings")
    .insert(payload)
    .select(
      `id, reference_number, scheduled_start, status, direction,
       site_id, bay_id,
       site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code),
       bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,code,color)`
    )
    .maybeSingle();
}

export async function updateBooking(id, values, currentUserId) {
  const payload = await buildBookingPayload(values, currentUserId, false);
  return supabase.from("lbs_bookings").update(payload).eq("id", id).select().maybeSingle();
}

export async function deleteBooking(id) {
  // NOTE: `.select()` is required to force Prefer: return=representation.
  // Without it Supabase returns 204 No Content and the JS client throws
  // "Failed to construct 'Response': Response with null body status
  // cannot have body" on delete. Same class of bug we hit earlier with
  // resequenceBays / updateBay.
  return supabase.from("lbs_bookings").delete().eq("id", id).select();
}

export async function getBookingLifecycle(bookingId) {
  const { data, error } = await supabase
    .from("lbs_v_booking_lifecycle")
    .select("lifecycle_state, is_overdue")
    .eq("booking_id", bookingId)
    .maybeSingle();
  return error ? null : data;
}

export async function listBookingLifecycles(ids) {
  if (!ids || ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from("lbs_v_booking_lifecycle")
    .select("booking_id, lifecycle_state, is_overdue")
    .in("booking_id", ids);
  if (error) {
    console.error("[lifecycle] view query failed:", error);
    return new Map();
  }
  if (!data || data.length === 0) {
    console.warn("[lifecycle] view returned 0 rows for", ids.length, "booking ids");
  }
  const m = new Map();
  for (const r of (data || [])) m.set(r.booking_id, r);
  return m;
}

export async function rebookBooking(originalId, { newStart, newEnd, resetDriver = false }) {
  const { data: orig, error: e1 } = await supabase
    .from("lbs_bookings")
    .select("*")
    .eq("id", originalId)
    .maybeSingle();
  if (e1 || !orig) return { error: e1 || { message: "Original not found" } };
  const clone = { ...orig };
  // ── Identity / row keys — the new row gets its own id + timestamps
  //     and reference_number is regenerated by the set_booking_reference()
  //     trigger on insert (`""` triggers the fill; see migration 040).
  delete clone.id;
  delete clone.created_at;
  delete clone.updated_at;
  // ── Lifecycle timestamps that must NOT carry over from the source
  //     booking (else the new draft would appear as if it were already
  //     checked-in / approved / completed by someone weeks ago). Any
  //     key not on this row is a no-op `delete`, so listing everything
  //     the schema might have is safe. Fix for P2 flagged by the R-1..R-7
  //     final code review (2026-07-14) — the prior version only cleared
  //     3 timestamps and left the audit fields dangling.
  delete clone.checkin_time;
  delete clone.checkout_time;
  delete clone.arrival_time;
  delete clone.approved_at;
  delete clone.approved_by;
  delete clone.completed_at;
  delete clone.cancelled_at;
  delete clone.no_show_at;
  delete clone.checked_in_at;
  delete clone.checked_in_by;
  delete clone.arrival_capture_method;
  delete clone.hold_set_at;
  delete clone.hold_set_by_user_id;
  delete clone.qa_confirmed_at;
  delete clone.qa_confirmed_by_user_id;
  // ── Recurring plumbing — a rebook is an ad-hoc manual clone, NOT a
  //     new occurrence of the source pattern. Carrying these over
  //     would create a phantom "linked-but-orphaned" recurring row
  //     that the pattern-regeneration cron doesn't reconcile.
  delete clone.recurring_pattern_id;
  delete clone.is_recurring;
  // ── Optional: reset the assigned driver (offered via RebookDialog).
  if (resetDriver) {
    clone.assigned_driver_id = null;
    clone.driver_tba = false;
  }
  clone.reference_number = "";
  clone.status = "draft";
  clone.scheduled_start = newStart || orig.scheduled_start;
  clone.scheduled_end   = newEnd   || orig.scheduled_end;
  clone.rebooked_from_id = originalId;
  return supabase.from("lbs_bookings").insert(clone).select().maybeSingle();
}
